import { useQuery } from '@tanstack/react-query';
import { getMyOpsUser } from '../../api/endpoints/ops';
import { getLaneHint, getProfitSummary } from '../../api/endpoints/profit';
import { historyQueryPolicy, staticQueryPolicy } from '../../api/queryPolicies';
import { isDemo } from '../../demo/flag';
import { isManager, opsUserKeys } from '../ownership/opsUser';
import type { ProfitDays, ProfitGroupBy } from '../../api/types';

export const profitSummaryKey = (days: ProfitDays, groupBy: ProfitGroupBy) =>
  ['admin', 'profit', 'summary', days, groupBy] as const;
export const laneHintKey = (originCity: string, destinationCity: string) =>
  ['admin', 'profit', 'lane-hint', originCity, destinationCity] as const;

/** One request per tab and period; past orders, so no polling. */
export function useProfitSummary(days: ProfitDays, groupBy: ProfitGroupBy, enabled: boolean) {
  return useQuery({
    queryKey: profitSummaryKey(days, groupBy),
    queryFn: () => getProfitSummary(days, groupBy),
    enabled,
    ...historyQueryPolicy,
  });
}

/**
 * The lane hint is decoration (P1): one call per city pair, no retry, and a failure is silent.
 * The key holds both cities, so the call waits until both are known.
 */
export function useLaneHint(originCity: string, destinationCity: string, enabled: boolean) {
  return useQuery({
    queryKey: laneHintKey(originCity, destinationCity),
    queryFn: () => getLaneHint(originCity, destinationCity, 90),
    enabled,
    retry: false,
    ...historyQueryPolicy,
  });
}

/**
 * True when per-order profit may show (#300): demo world and OPS_MANAGER (P9). Shares the signed-in user's
 * cache with useOpsUser; outside demo it asks nothing.
 */
export function useCanSeeProfit(): boolean {
  const demo = isDemo();
  const { data } = useQuery({ queryKey: opsUserKeys.me, queryFn: getMyOpsUser, enabled: demo, ...staticQueryPolicy });
  return demo && isManager(data);
}
